/**
 * A single express route that goes through the cart.
 *
 * Shopify's wallet buttons submit the product form directly, so they can only ever
 * buy the main product, and they render inside an iframe, so a click cannot be
 * intercepted to add anything alongside it. This replaces them with one button:
 * whatever is selected lands in the cart and the customer continues in checkout,
 * where the same payment methods are offered.
 *
 * The bundle block used to do this replacement itself, which meant products
 * without a bundle kept the wallet buttons. The behaviour now lives here so every
 * product gets the same button; the bundle block only contributes its selection.
 */
class ExpressCheckout extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#button?.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
  }

  /**
   * The bundle component owns its own selection state, so its items are read off
   * it when one is present. Absent a bundle this is simply empty.
   *
   * @returns {Array<{id: number, quantity: number, properties: object | undefined}>}
   */
  #bundleItems() {
    const bundle = this.closest('.shopify-section')?.querySelector('bundle-offer-component');
    if (!bundle || typeof bundle.getSelectedItems !== 'function') return [];

    return bundle.getSelectedItems().map((item) => ({
      id: Number(item.variantId),
      quantity: item.quantity,
      properties: item.properties,
    }));
  }

  #onClick = async () => {
    const button = this.#button;
    if (!button || button.disabled) return;

    const form = this.closest('.shopify-section')?.querySelector('product-form-component form');
    const variantId = form?.querySelector('input[name="id"]');
    if (!(variantId instanceof HTMLInputElement)) return;

    const quantityInput = form?.querySelector('input[name="quantity"]');
    const items = [
      { id: Number(variantId.value), quantity: Number(quantityInput?.value) || 1 },
      ...this.#bundleItems(),
    ];

    button.disabled = true;
    this.dataset.busy = 'true';

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!response.ok) throw new Error(`Cart responded with ${response.status}`);

      window.location.href = `${Theme.routes.cart_url}/checkout`;
    } catch (error) {
      console.error('Express checkout failed', error);
      button.disabled = false;
      this.removeAttribute('data-busy');
    }
  };
}

if (!customElements.get('express-checkout')) {
  customElements.define('express-checkout', ExpressCheckout);
}
